import { useState, useContext } from 'react';
import { Box, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttList,
    SttChipPForm,
    SttChipPaymentStatus,
    SttLabelDate,
} from '../all';
import PaymentModal from '../../components/paymentModal/PaymentModal';
import { formatPriceByLocale, fullName } from '../../lib/utils';
import constants from '../../config/constants';

type User = {
    name?: string;
    surname?: string;
};

type Payment = {
    id: number;
    user: User;
    created: string;
    concept: string;
    status: string;
    paymentForm?: string;
    price: number;
    trash?: boolean;
    priceShape?: {
        currency?: string;
        locale?: string;
    };
};

type Props = {
    paper?: boolean;
    payments: Payment[];
    clickable?: boolean;
    onClose?: () => void;
};

/**
 *    Payment list
 *    ============
 *
 *    Props
 *
 *        * array payments
 *        * boolean clickable [false]: cuando se hace click se abre el pago
 */
export function SttPaymentList({
    paper = true,
    payments,
    clickable = false,
    onClose,
    ...rest
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [idPayModal, setIdPayModal] = useState<number | null>(null);

    const handleOnSelect = clickable
        ? (pay: Payment) => {
              setIdPayModal(pay.id);
          }
        : undefined;

    return (
        <>
            <SttList
                paper={paper}
                title={t('payment.payments')}
                onSelect={handleOnSelect}
                data={payments.map((pay: any) => ({
                    caption: (
                        <Grid
                            container
                            spacing={3}
                            style={{
                                color: pay.trash ? '#ff5131' : 'none',
                            }}
                        >
                            <Grid size={{ md: 6 }}>
                                {fullName(pay.user)}
                                <Box
                                    style={{
                                        fontSize: 13,
                                        color: '#aaa',
                                    }}
                                >
                                    <b>
                                        <SttLabelDate
                                            date={pay.created}
                                            timezoneName={
                                                cxt?.sc?.timezone?.name
                                            }
                                            locale={cxt?.lang?.key}
                                        />
                                    </b>
                                    , {pay.concept}
                                </Box>
                            </Grid>
                            <Grid sx={{ textAlign: 'center' }} size={{ md: 3 }}>
                                {pay.status ===
                                constants.payment.status.paid.name ? (
                                    <SttChipPForm pf={pay.paymentForm} />
                                ) : (
                                    <SttChipPaymentStatus status={pay.status} />
                                )}
                            </Grid>
                            <Grid sx={{ textAlign: 'right' }} size={{ md: 3 }}>
                                <b>
                                    {formatPriceByLocale(
                                        pay.price,
                                        pay?.priceShape?.currency,
                                        pay?.priceShape?.locale,
                                    )}
                                </b>
                            </Grid>
                        </Grid>
                    ),
                    objToSelect: pay,
                }))}
                {...rest}
            />

            {idPayModal && (
                <PaymentModal
                    idPayment={idPayModal}
                    onClose={() => {
                        setIdPayModal(null);
                        onClose?.();
                    }}
                />
            )}
        </>
    );
}
